import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
	API_ENDPOINTS,
	BottomAnchoredControls,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	CardPlaceholder,
	customToast,
	Icon,
	Icons,
	Modal,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	route,
	StringHelpers,
	useAuthorisationContext,
	useDisabledContext,
	useLangContext,
	Variants,
} from 'carrier-fe';
import { useNavigate } from 'react-router-dom';
import { CarouselSlide, CarouselSlideAction } from './types';
import SortableSlide from './components/SortableSlide';

type ModalType = 'archive' | 'restore' | null;

function CarouselSlidesList() {
	const navigate = useNavigate();
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const [items, setItems] = useState<CarouselSlide[]>([]);
	const [isFetching, setIsFetching] = useState<boolean>(false);
	const [showArchived, setShowArchived] = useState<boolean>(false);
	const [activeModal, setActiveModal] = useState<ModalType>(null);
	const [selectedSlide, setSelectedSlide] = useState<CarouselSlide | null>(
		null
	);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
	);

	const modelName = crud?.models?.carousel_slide || 'Carousel Slide';

	const title = useMemo(
		() =>
			StringHelpers.title(
				replaceKeyWithValue(
					crud?.pages?.index?.title || ':model List',
					'model',
					modelName
				)
			),
		[crud, modelName]
	);

	useEffect(() => {
		fetchSlides();
	}, [showArchived]);

	const fetchSlides = async () => {
		if (isFetching) return;
		setIsFetching(true);
		try {
			const params: Record<string, string> = { 'page[size]': '500' };
			if (showArchived) {
				params['filter[trashed]'] = 'with';
			}
			const { data } = await axios.get(
				route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.INDEX),
				{ params }
			);
			const raw = data?.data?.rows ?? data?.data ?? [];
			const rows: CarouselSlide[] = Array.isArray(raw) ? [...raw] : [];
			rows.sort((a, b) => {
				if (!!a.deleted_at !== !!b.deleted_at) {
					return a.deleted_at ? 1 : -1;
				}
				return a.order - b.order;
			});
			setItems(rows);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error fetching carousel slides.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	const persistOrder = async (nextItems: CarouselSlide[]) => {
		const payload = nextItems
			.filter((s) => !s.deleted_at)
			.map((s, index) => ({ id: s.id, order: index }));

		try {
			setDisabled(true);
			const { data } = await axios.patch(
				route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.REORDER),
				{ slides: payload }
			);
			customToast({
				title: data?.message || 'Order updated.',
				variant: Variants.Success,
			});
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error updating order. Reverting.',
				variant: Variants.Danger,
			});
			fetchSlides();
		} finally {
			setDisabled(false);
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const activeSlide = items.find((i) => i.id === active.id);
		if (activeSlide?.deleted_at) return;

		const oldIndex = items.findIndex((i) => i.id === active.id);
		const newIndex = items.findIndex((i) => i.id === over.id);
		if (oldIndex < 0 || newIndex < 0) return;

		const reordered = arrayMove(items, oldIndex, newIndex).map(
			(item, index) => ({
				...item,
				order: item.deleted_at ? item.order : index,
			})
		);
		setItems(reordered);
		persistOrder(reordered);
	};

	const handleAction = (action: CarouselSlideAction, slide: CarouselSlide) => {
		if (action === 'edit') {
			navigate(`/carousel-slides/${slide.id}/edit`);
			return;
		}
		setSelectedSlide(slide);
		setActiveModal(action);
	};

	const closeModal = () => {
		setActiveModal(null);
		setSelectedSlide(null);
	};

	const archiveSlide = async () => {
		if (!selectedSlide) return;
		setDisabled(true);
		try {
			const { data } = await axios.delete(
				route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.DELETE, {
					carouselSlide: selectedSlide.id,
				})
			);
			customToast({
				title: data?.message || 'Carousel slide archived.',
				variant: Variants.Success,
			});
			fetchSlides();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error archiving carousel slide.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const restoreSlide = async () => {
		if (!selectedSlide) return;
		setDisabled(true);
		try {
			const { data } = await axios.patch(
				route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.RESTORE, {
					carouselSlide: selectedSlide.id,
				})
			);
			customToast({
				title: data?.message || 'Carousel slide restored.',
				variant: Variants.Success,
			});
			fetchSlides();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error restoring carousel slide.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const canCreate = hasPermissionAccess(
		Permissions.ADMIN_CAROUSEL_SLIDE_STORE
	);

	return (
		<PageLayout title={title}>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<Button
					label={
						showArchived
							? fields?.hide_archived || 'Hide archived'
							: fields?.show_archived || 'Show archived'
					}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					onClick={() => setShowArchived((v) => !v)}
					disabled={disabled}
				/>
				{canCreate && (
					<Button
						label={StringHelpers.title(
							crud?.buttons?.create?.default || 'Create'
						)}
						onClick={() => navigate('/carousel-slides/create')}
						variant={Variants.Info}
						disabled={disabled}
					/>
				)}
			</div>

			{isFetching ? (
				<>
					<CardPlaceholder />
					<CardPlaceholder />
					<CardPlaceholder />
				</>
			) : items.length === 0 ? (
				<Card>
					<CardBody className="text-center text-muted py-5">
						<Icon icon={Icons.IMAGE} className="mb-3" />
						<div>
							{fields?.no_results ||
								'No carousel slides yet. Create your first slide to get started.'}
						</div>
					</CardBody>
				</Card>
			) : (
				<DndContext sensors={sensors} onDragEnd={handleDragEnd}>
					<SortableContext
						items={items.map((i) => i.id)}
						strategy={verticalListSortingStrategy}
					>
						{items.map((slide) => (
							<SortableSlide
								key={slide.id}
								slide={slide}
								disabled={disabled}
								onAction={handleAction}
							/>
						))}
					</SortableContext>
				</DndContext>
			)}

			<BottomAnchoredControls>
				<Button
					label={fields?.refresh || 'Refresh'}
					onClick={fetchSlides}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					disabled={disabled || isFetching}
				/>
			</BottomAnchoredControls>

			<Modal
				open={activeModal === 'archive'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.archive?.title || 'Archive :name',
						'name',
						modelName
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.archive?.submitting || 'Archiving...'
						: crud?.buttons?.archive?.default || 'Archive'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				variant={Variants.Primary}
				onConfirm={archiveSlide}
			>
				{crud?.modals?.archive?.description?.map(
					(item: string, key: number) => (
						<p key={key}>
							{replaceKeyWithValue(
								item,
								'name',
								StringHelpers.lower(modelName)
							)}
						</p>
					)
				)}
			</Modal>

			<Modal
				open={activeModal === 'restore'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.restore?.title || 'Restore :name',
						'name',
						modelName
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.restore?.submitting || 'Restoring...'
						: crud?.buttons?.restore?.default || 'Restore'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				variant={Variants.Primary}
				onConfirm={restoreSlide}
			>
				{crud?.modals?.restore?.description?.map(
					(item: string, key: number) => (
						<p key={key}>
							{replaceKeyWithValue(
								item,
								'name',
								StringHelpers.lower(modelName)
							)}
						</p>
					)
				)}
			</Modal>
		</PageLayout>
	);
}

export default CarouselSlidesList;
